import { Field, Form, Formik } from 'formik'
import React from 'react'
import InputField from '../form/fields/input'
import { useUpdateMutation } from '../../queries/job-titles'
import { JobTitle } from '../../api/job-titles'
import { Box, Columns, Element, Heading, Message } from 'react-bulma-components'
import { RequestButton } from '../request-components/request-components'
import { InterimAgency } from '../../api/organizations'
import { compareDocsById, getDocIdAsString } from '../../utils/documents'
import { getJobTitleInterimAgencySettings } from '../../utils/job-titles'
import CheckboxField from '../form/fields/checkbox'
import { useSmicQuery } from '../../queries/smic'

interface JobTitleLaborCoefficientFormProps {
  jobTitle: JobTitle
  interimAgency: InterimAgency
}

const JobTitleLaborCoefficientForm: React.FC<JobTitleLaborCoefficientFormProps> = ({
  jobTitle,
  interimAgency,
}) => {
  const updateMutation = useUpdateMutation()
  const smicQuery = useSmicQuery(new Date(new Date().setHours(12, 0, 0, 0)))
  const interimAgencySettings = getJobTitleInterimAgencySettings(jobTitle, interimAgency)

  const currentSmicValue = smicQuery.data?.value

  return (
    <Formik
      initialValues={{
        delegationValue: interimAgencySettings?.coefficients.delegationValue,
        managementValue: interimAgencySettings?.coefficients.managementValue,
        hourlyRateSettings: interimAgencySettings?.hourlyRateSettings || {
          min: null,
          max: null,
          useSmic: true,
        },
      }}
      enableReinitialize
      validate={values => {
        const errors: any = {}
        if (!values.delegationValue) errors.delegationValue = 'Champ requis'
        if (!values.managementValue) errors.managementValue = 'Champ requis'

        // Validation des taux horaires
        if (!values.hourlyRateSettings.useSmic) {
          if (!values.hourlyRateSettings.min) {
            errors['hourlyRateSettings.min'] = 'Champ requis'
          } else if (values.hourlyRateSettings.min < (currentSmicValue || 0)) {
            errors['hourlyRateSettings.min'] =
              `Le minimum doit être supérieur à ${currentSmicValue} €`
          }
          if (!values.hourlyRateSettings.max) {
            errors['hourlyRateSettings.max'] = 'Champ requis'
          }
          if (
            values.hourlyRateSettings.min &&
            values.hourlyRateSettings.max &&
            values.hourlyRateSettings.min > values.hourlyRateSettings.max
          ) {
            errors['hourlyRateSettings.min'] = 'Le minimum doit être inférieur au maximum'
          }
        }
        return errors
      }}
      onSubmit={values => {
        const isNewInterimAgencySettings = jobTitle.interimAgencySettings.find(l =>
          compareDocsById(l.interimAgency, interimAgency),
        )
        const updatedInterimAgencySettings = jobTitle.interimAgencySettings.map(l => {
          if (compareDocsById(l.interimAgency, interimAgency)) {
            return {
              ...l,
              coefficients: {
                delegationValue: values.delegationValue as number,
                managementValue: values.managementValue as number,
              },
              hourlyRateSettings: values.hourlyRateSettings,
            }
          }
          return l
        })
        if (!isNewInterimAgencySettings) {
          updatedInterimAgencySettings.push({
            interimAgency: getDocIdAsString(interimAgency),
            coefficients: {
              delegationValue: values.delegationValue as number,
              managementValue: values.managementValue as number,
            },
            hourlyRateSettings: values.hourlyRateSettings,
          })
        }
        updateMutation.mutate({
          id: jobTitle._id,
          interimAgencySettings: updatedInterimAgencySettings,
        })
      }}
    >
      {({ values }) => {
        return (
          <Form autoComplete="off">
            <Heading size={4} mb={5}>
              {jobTitle.name} / {interimAgency.name}
            </Heading>
            <Heading size={5} mb={2}>
              Coefficients de main d'oeuvre
            </Heading>
            <Box>
              <Columns>
                <Columns.Column>
                  <Field
                    label={'Délégation'}
                    name={`delegationValue`}
                    component={InputField}
                    type="number"
                    required
                    step="0.1"
                  />
                </Columns.Column>
                <Columns.Column>
                  <Field
                    label={'Gestion'}
                    name={`managementValue`}
                    component={InputField}
                    type="number"
                    required
                    step="0.1"
                  />
                </Columns.Column>
              </Columns>
            </Box>
            <Element mt={4}>
              <Heading size={5} mb={2}>
                Taux horaire
              </Heading>
              <Box>
                <Element>
                  <Field
                    text={<Element renderAs="strong">Utiliser le SMIC</Element>}
                    name={`hourlyRateSettings.useSmic`}
                    component={CheckboxField}
                    type="checkbox"
                    options={[
                      { label: 'Oui', value: true },
                      { label: 'Non', value: false },
                    ]}
                  />
                </Element>
                {values.hourlyRateSettings.useSmic && (
                  <Message color="info" mt={2}>
                    <Message.Body>
                      <Element renderAs="p">
                        Le cout horaire de base des journées de travail sera le SMIC du jour.
                        <br />
                        SMIC actuel : {currentSmicValue} €
                      </Element>
                    </Message.Body>
                  </Message>
                )}

                {!values.hourlyRateSettings.useSmic && (
                  <Element mt={2}>
                    <Message color="info">
                      <Message.Body>
                        <Element renderAs="p">
                          Le minimum doit être supérieur au SMIC actuel ({currentSmicValue} €).
                        </Element>
                        <Element renderAs="p">
                          Le smic évolue dans le temps, il est donc possible que le minimum devienne
                          inférieur au SMIC. Dans ce cas, le coût horaire sera calculé avec le SMIC
                          du jour.
                        </Element>
                      </Message.Body>
                    </Message>
                    <Columns>
                      <Columns.Column>
                        <Field
                          label={'Minimum €'}
                          name={`hourlyRateSettings.min`}
                          component={InputField}
                          type="number"
                          required
                        />
                      </Columns.Column>
                      <Columns.Column>
                        <Field
                          label={'Maximum €'}
                          name={`hourlyRateSettings.max`}
                          component={InputField}
                          type="number"
                          required
                        />
                      </Columns.Column>
                    </Columns>
                  </Element>
                )}
              </Box>
            </Element>
            <Element mt={5}>
              <RequestButton color="primary" type="submit" mutation={updateMutation}>
                Mettre à jour
              </RequestButton>
            </Element>
          </Form>
        )
      }}
    </Formik>
  )
}
export default JobTitleLaborCoefficientForm
